import { time } from "console"
import { urlToHttpOptions } from "url"

type State = {
    hiddenInput: HTMLInputElement,
    searchInput: HTMLInputElement,
    placeholder: HTMLDivElement,
    dropdownMenu: HTMLDivElement,
    remove: HTMLDivElement,
    prompt: string,
    options: Array<HTMLDivElement>,
    customValue: Boolean,
}


document.addEventListener('DOMContentLoaded', () => {
    initSelect2()
})

export default function initSelect2(
    wrappers: HTMLElement[]|undefined = undefined
): void
{
    if (wrappers === undefined) {
        wrappers = Array.from(document.querySelectorAll<HTMLElement>('.select2-wrapper'))
    }

    wrappers.forEach(wrapper => {

        const hiddenInput = wrapper.querySelector<HTMLInputElement>('[type="select2"]')
        const searchInput = wrapper.querySelector<HTMLInputElement>('.select2-search-input')
        const placeholder = wrapper.querySelector<HTMLDivElement>('.select2-placeholder-text')
        const dropdownMenu = wrapper.querySelector<HTMLDivElement>('.dropdown-menu .dropdown-item-wrapper')
        const customValue = searchInput?.classList.contains('custom-value') || false
        const remove = wrapper.querySelector<HTMLDivElement>('.select2-remove')

        if (hiddenInput && searchInput && placeholder && dropdownMenu && remove) {

            const state: State = {
                hiddenInput: hiddenInput,
                searchInput: searchInput,
                placeholder: placeholder,
                dropdownMenu: dropdownMenu,
                remove: remove,
                prompt: placeholder.getAttribute('placeholder') || '',
                options: [],
                customValue: customValue
            }


            remove.addEventListener('click', e => {
                e.stopPropagation()
                state.placeholder.innerHTML = state.prompt
                state.searchInput.value = ''
                state.hiddenInput.value = ''
                state.remove.classList.add('d-none')
                const event = new Event('change', { bubbles: true, cancelable: true });
                state.hiddenInput.dispatchEvent(event)
            })

            wrapper.addEventListener("focus", () => {
                openDropdown(wrapper)
            })

            wrapper.addEventListener("keydown", (e) => {
                if (e.key === 'Tab') {
                    closeDropdown(wrapper)
                }
                // TODO zde by se dalo dopsat ovládání šipkami a potvrzení enterem
            })


            wrapper.querySelectorAll<HTMLDivElement>('.dropdown-item').forEach(item => {
                addOption(state, item.getAttribute('value') || '', item.innerHTML)
            })

            handleSearchInputChange(state)

            state.searchInput.addEventListener('input', () => handleSearchInputChange(state))
        }
    })
}

function openDropdown(wrapper: HTMLElement): void
{
    wrapper.setAttribute('aria-expanded', 'true')
    wrapper.classList.add('show')
    wrapper.querySelector('.dropdown-menu')?.classList.add('show')
}

function closeDropdown(wrapper: HTMLElement): void
{
    wrapper.setAttribute('aria-expanded', 'false')
    wrapper.classList.remove('show')
    wrapper.querySelector('.dropdown-menu')?.classList.remove('show')
}

function addOption(state: State, value: string, html: string): void
{
    const newItem = document.createElement('div')
    newItem.setAttribute('value', value)
    newItem.classList.add('dropdown-item')
    newItem.innerHTML = html
    newItem.addEventListener('click', () => handleItemClick(state, newItem))
    if(value === '##custom') {
        state.options.unshift(newItem)
    }
    else {
        state.options.push(newItem)
    }
}

let timeout: NodeJS.Timeout

function handleSearchInputChange(state: State): void
{
    const searchedText = state.searchInput.value
    const ajaxActionUrl = state.hiddenInput.dataset.ajaxActionUrl
    let exactMatch = false

    if (ajaxActionUrl) {
        state.options = []

        if (timeout) {
            clearTimeout(timeout)
        }

        timeout = setTimeout(() => {
            fetchItems(state, searchedText, ajaxActionUrl)
                .then(() => afterFilter(state, searchedText, true))
        }, 300)
    } else {
        state.options.forEach(option => {
            const optionText = option.innerText

            if (optionText.toLowerCase().includes(searchedText.toLowerCase())) {
                option.style.display = 'block'
                if (searchedText === optionText) {
                    exactMatch = true
                }
            }
            else {
                option.style.display = 'none'
            }
        })
        afterFilter(state, searchedText, exactMatch)
    }
}


function afterFilter(state: State, searchedText: string, exactMatch: boolean): void
{
    // Vymažeme z pole všechny custom položky
    state.options = state.options.filter(option => option.getAttribute('value') !== '##custom')

    // Pokud nebyla v položkách nalezena přesná shoda tak vložíme novou položku
    if (searchedText && state.customValue && !exactMatch) {
        addOption(state, '##custom', searchedText)
    }

    state.dropdownMenu.innerHTML = ''
    state.options.forEach(option => {
        state.dropdownMenu.append(option)
    })
}


function handleItemClick(state: State, option: HTMLElement): void
{
    if(state.customValue) {
        state.hiddenInput.value = option.innerText
    }
    else {
        state.hiddenInput.value = option.getAttribute('value') || ''
    }

    const event = new Event('change', { bubbles: true, cancelable: true });
    state.hiddenInput.dispatchEvent(event)

    state.remove.classList.remove('d-none')
    state.searchInput.value = ''
    state.placeholder.innerText = option.innerText
    handleSearchInputChange(state)
}


let lastController: AbortController|null = null

async function fetchItems(state: State, searchedText: string, ajaxActionUrl: string): Promise<HTMLElement[]>
{
    if (lastController) {
        lastController.abort()
    }

    const controller = new AbortController()
    const signal = controller.signal
    lastController = controller

    const url = ajaxActionUrl.replace('select2_placeholder', searchedText)

    try {
        await fetch(url, { signal })
        .then(res => res.json())
        .then(obj => {
            const items = Object.entries(obj)

            items.sort((a, b) => {
                return a[1].localeCompare(b[1])
            })

            let i = 1
            items.forEach(item => {
                if (++i < 100) {
                    addOption(state, item[0], item[1])
                }
            })
        })
    } catch (err) {
        if (err.name !== 'AbortError') {
            console.error('Fetch error:', err);
        }
    }

    return []
}
